<template>
  <div id="app">
    <div v-if="profiles.length !== 0" class="header">
      <h1>
        <!-- Remove all profiles. -->
        <a
          @click="
            profiles = [];
            loadingFriends = true;
            friendsError = false;
          "
        >
          What Can We Play
        </a>
      </h1>
    </div>
    <!-- Show initial search prompt -->
    <div v-if="profiles.length === 0">
      <Logo title="What Can We Play" />
      <Search @addProfile="addProfile" @getFriends="getFriends" />
    </div>

    <!-- Show profiles -->
    <div v-if="profiles.length > 0" class="profiles">
      <Container
        :title="profiles.length > 1 ? 'Party' : 'Profile'"
        :viewable="true"
      >
        <div v-if="profiles.length == 1">
          <FirstProfile :profile="profiles[0]" @removeProfile="removeProfile" />
        </div>
        <div v-if="profiles.length > 1">
          <Profiles :profiles="profiles" @removeProfile="removeProfile" />
        </div>
      </Container>
    </div>

    <!-- Show friends -->
    <div v-if="profiles.length > 0">
      <Container
        title="Friends"
        :viewable="true"
        :count="profiles[0].friends.length"
      >
        <div v-if="loadingFriends & !friendsError" class="friends-loading">
          <p>Getting friends list.</p>
          <div class="lds-ring">
            <div />
            <div />
            <div />
            <div />
          </div>
        </div>
        <div v-if="!loadingFriends & friendsError">
          <p>
            There was an error retrieving friends list:
            {{ friendsErrorMessage }}
          </p>
        </div>
        <div v-if="!loadingFriends & (profiles.length > 0)">
          <Friends :friends="profiles[0].friends" @addFriend="addProfile" />
        </div>
      </Container>
    </div>

    <!-- Show games -->
    <div v-if="profiles.length > 0">
      <Container
        v-if="commonGames(profiles).length > 0"
        :title="profiles.length === 1 ? 'Games' : 'Common Games'"
        :count="commonGames(profiles).length"
        :viewable="true"
      >
        <Games :games="commonGames(profiles)" />
      </Container>
      <Container
        v-if="commonGames(profiles).length === 0"
        :title="profiles.length === 1 ? 'Games' : 'Common Games'"
        :viewable="true"
      >
        <div id="no-common">
          No common games.
          <span class="icon is-small is-left">
            <i class="fas fa-sad-tear" />
          </span>
        </div>
      </Container>
    </div>
  </div>
</template>

<script>
import Logo from "./components/Logo.vue";
import Search from "./components/Search.vue";
import FirstProfile from "./components/FirstProfile.vue";
import Profiles from "./components/Profiles.vue";
import Friends from "./components/Friends.vue";
import Games from "./components/Games.vue";

import Container from "./components/Container.vue";

import axios from "axios";

export default {
  name: "App",
  components: {
    Search,
    Logo,
    Profiles,
    Friends,
    Container,
    FirstProfile,
    Games,
  },
  data: function () {
    return {
      profiles: [],
      showLogo: true,
      loadingFriends: true,
      friendsError: false,
      friendsErrorMessage: "",
      apiURL: process.env.VUE_APP_API_URL,
    };
  },
  methods: {
    /**
     * Create array of common/unique games across party profiles. If only 1
     * profile passed, returns all games.
     * @param profiles - Profiles to check for common games across.
     */
    commonGames: function (profiles) {
      let common_games = [];
      common_games = profiles[0].games;
      for (const profile of profiles.slice(1, profiles.length)) {
        common_games = common_games.filter((n) =>
          profile.games.some((n2) => n.name == n2.name)
        );
      }
      return common_games;
    },

    /**
     * Add profile to party array (profiles variable). If party empty, add
     * profile. If party not empty, check if profile already exists in party
     * before adding.
     * @param {object} profile - Profile to be added.
     */
    addProfile: async function (profile) {
      // Check if profile already in party.
      const notInParty = (existingProfile) =>
        existingProfile.steamid !== profile.steamid;

      if (this.profiles.length === 0) {
        this.profiles.push(profile);
      } else if (this.profiles.every(notInParty)) {
        // Check if profile already part of party, if so don't add.
        const games = await this.getGames(profile.steamid);
        profile = this.addGamesToProfile(games, profile);
        this.profiles.push(profile);
      }
    },

    /**
     * Remove existing profile from list of added profiles.
     * @param profileToRemove - profile passed from child on click
     */
    removeProfile: function (profileToRemove) {
      const index = this.profiles.indexOf(profileToRemove);
      //  Don't allow initial profile to be removed unless it is
      // the only profile.
      if ((index === 0) & (this.profiles.length === 1)) {
        this.profiles.splice(index, 1);
        this.loadingFriends = true;
        this.friendsError = false;
      } else if ((index > -1) & (index !== 0)) {
        this.profiles.splice(index, 1);
      }
    },

    /**
     * Return games for given Steam ID.
     * @param {string} steamID - Steam profile ID.
     */
    getGames: async function (steamID) {
      return axios
        .get(`${this.apiURL}/games/${steamID}`)
        .then((response) => response.data);
    },

    getFriends: async function (steamID) {
      if (this.profiles[0].friends.length === 0) {
        return axios
          .get(`${this.apiURL}/friends/${steamID}`)
          .then((response) => {
            this.loadingFriends = false;
            this.profiles[0].friends = response.data.friends;
          })
          .catch((err) => {
            const status = err.response.status;
            const data = err.response.data;
            if ((status === 404) | (status === 400) | (status === 500)) {
              this.friendsErrorMessage = data["error"];
            }
            this.loadingFriends = false;
            this.friendsError = true;
          });
      } else {
        // use sample data for try me button
        this.loadingFriends = false;
      }
    },

    /**
     * Merge games into profile being added to party.
     * @param {object} games - Games count and array of games.
     * @param {object} profile - Profile details.
     */
    addGamesToProfile: function (games, profile) {
      return { ...profile, ...games };
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* text-align: center; */
  color: #2c3e50;
  margin-top: 2em;
}
.profiles {
  margin-bottom: 1em;
}
.header h1 {
  font-size: 2em;
  margin: 0 0 1em;
  text-align: center;
}

.header a {
  text-decoration: none;
  color: #2c3e50;
}

#no-common {
  color: grey;
  /* text-align: center; */
}

.lds-ring {
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
  margin-left: 1em;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 20px;
  height: 20px;
  margin: 2px;
  border: 2px solid lightgray;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: lightgray transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.friends-loading {
  display: flex;
  flex-direction: row;
}

@media only screen and (max-width: 640px) {
  .header h1 {
    font-size: 1.8em;
    margin: 0 0 0.5em;
  }
}
</style>
